import React, { useState, useEffect, useCallback } from "react";
import { DetailRow, DetailSection } from "../../../ui/DetailRow";
import { AppButton, StatusBadge } from "../../../ui";
import { Globe, CheckCircle, AlertTriangle, Copy, Trash2, RefreshCw, Shield } from "lucide-react";
import apiClient from "../../../../utils/apiClient";

const DomainTab = ({ institute }) => {
  const [domain, setDomain] = useState("");
  const [verified, setVerified] = useState(false);
  const [inputDomain, setInputDomain] = useState("");
  const [certStatus, setCertStatus] = useState(""); // '' | 'pending' | 'issued' | 'failed'
  const [certValidation, setCertValidation] = useState({});
  const [cloudFrontAdded, setCloudFrontAdded] = useState(false);
  const [cloudFrontDomain, setCloudFrontDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [steps, setSteps] = useState([]);

  const fetchDomain = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/sa/institutes/${institute._id}/custom-domain`);
      setDomain(res.data.customDomain || "");
      setVerified(res.data.domainVerified || false);
      setInputDomain(res.data.customDomain || "");
      setCertStatus(res.data.certStatus || "");
      setCertValidation(res.data.certValidation || {});
      setCloudFrontAdded(res.data.cloudFrontAdded || false);
      setCloudFrontDomain(res.data.cloudFrontDomain || "");
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [institute._id]);

  useEffect(() => {
    fetchDomain();
  }, [fetchDomain]);

  const handleSave = async () => {
    if (!inputDomain.trim()) return;
    try {
      setSaving(true);
      setMessage(null);
      setSteps([]);
      const res = await apiClient.put(`/sa/institutes/${institute._id}/custom-domain`, {
        customDomain: inputDomain.trim(),
      });
      setDomain(res.data.customDomain);
      setVerified(res.data.domainVerified);
      setCertStatus(res.data.certStatus || "");
      setCertValidation(res.data.certValidation || {});
      setCloudFrontDomain(res.data.cloudFrontDomain || cloudFrontDomain);
      setCloudFrontAdded(false);
      setMessage({ type: "success", text: res.data.message });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to save domain." });
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    try {
      setVerifying(true);
      setMessage(null);
      setSteps([]);
      const res = await apiClient.post(`/sa/institutes/${institute._id}/custom-domain/verify`);
      setVerified(res.data.domainVerified);
      if (res.data.domainVerified) {
        setCertStatus("issued");
        setCloudFrontAdded(true);
      }
      setSteps(res.data.steps || []);
      setMessage({ type: "success", text: res.data.message });
    } catch (err) {
      const data = err.response?.data || {};
      setSteps(data.steps || []);
      setMessage({
        type: "error",
        text: data.message + (data.hint ? `\nHint: ${data.hint}` : ""),
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      setMessage(null);
      setSteps([]);
      await apiClient.put(`/sa/institutes/${institute._id}/custom-domain`, {
        customDomain: "",
      });
      setDomain("");
      setVerified(false);
      setInputDomain("");
      setCertStatus("");
      setCertValidation({});
      setCloudFrontAdded(false);
      setMessage({ type: "success", text: "Custom domain removed." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to remove domain." });
    } finally {
      setRemoving(false);
    }
  };

  const handleRefreshStatus = async () => {
    setMessage(null);
    await fetchDomain();
    setMessage({ type: "success", text: "Status refreshed." });
    setTimeout(() => setMessage(null), 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: "success", text: "Copied to clipboard!" });
    setTimeout(() => setMessage(null), 2000);
  };

  if (loading) {
    return <div className="sa-detail-tab"><div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div></div>;
  }

  const certLabel = certStatus === "issued" ? "Issued" : certStatus === "pending" ? "Pending" : certStatus === "failed" ? "Failed" : "—";
  const certColor = certStatus === "issued" ? "active" : certStatus === "pending" ? "trial" : certStatus === "failed" ? "suspended" : "inactive";

  return (
    <div className="sa-detail-tab">
      <DetailSection title="Custom Domain (White-Label)">
        <p className="sa-domain-tab__desc">
          Map a custom domain so this institute&apos;s portal loads on their own URL (e.g. <code>lms.theirinstitute.com</code>).
          The system will automatically provision an SSL certificate and configure CloudFront.
        </p>

        {/* Current status */}
        {domain && (
          <div className="sa-domain-tab__status-grid">
            <DetailRow
              icon={Globe}
              label="Domain"
              value={<code>{domain}</code>}
            />
            <DetailRow
              icon={Shield}
              label="SSL Certificate"
              value={<StatusBadge status={certColor}>{certLabel}</StatusBadge>}
            />
            <DetailRow
              label="CloudFront"
              value={
                <StatusBadge status={cloudFrontAdded ? "active" : "inactive"}>
                  {cloudFrontAdded ? "Configured" : "Not Added"}
                </StatusBadge>
              }
            />
            <DetailRow
              label="Overall"
              value={
                <StatusBadge status={verified ? "active" : "trial"}>
                  {verified ? "Live ✓" : "Setup In Progress"}
                </StatusBadge>
              }
            />
          </div>
        )}

        {/* Input for setting / changing domain */}
        <div className="sa-domain-tab__form">
          <label className="sa-domain-tab__label">Custom Domain</label>
          <div className="sa-domain-tab__input-row">
            <input
              type="text"
              className="form-control"
              placeholder="lms.theirinstitute.com"
              value={inputDomain}
              onChange={(e) => setInputDomain(e.target.value.toLowerCase())}
            />
            <AppButton
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={saving || !inputDomain.trim() || inputDomain.trim() === domain}
            >
              {saving ? "Saving..." : domain ? "Update" : "Save"}
            </AppButton>
          </div>
        </div>

        {/* DNS instructions — show when domain is set but not fully verified */}
        {domain && !verified && (
          <div className="sa-domain-tab__dns-box">
            <h6><AlertTriangle size={14} /> DNS Records Required</h6>
            <p>The institute needs to add <strong>2 DNS records</strong> with their domain provider:</p>
            <table className="sa-domain-tab__dns-table">
              <thead>
                <tr><th>#</th><th>Type</th><th>Name</th><th>Value</th><th>Purpose</th></tr>
              </thead>
              <tbody>
                {/* Record 1: CNAME to CloudFront */}
                <tr>
                  <td>1</td>
                  <td><code>CNAME</code></td>
                  <td>
                    <code>{domain}</code>
                    <button className="sa-domain-tab__copy" onClick={() => copyToClipboard(domain)} title="Copy">
                      <Copy size={12} />
                    </button>
                  </td>
                  <td>
                    <code>{cloudFrontDomain}</code>
                    <button className="sa-domain-tab__copy" onClick={() => copyToClipboard(cloudFrontDomain)} title="Copy">
                      <Copy size={12} />
                    </button>
                  </td>
                  <td>Points domain to CloudFront</td>
                </tr>
                {/* Record 2: ACM validation CNAME */}
                {certValidation?.name && (
                  <tr>
                    <td>2</td>
                    <td><code>CNAME</code></td>
                    <td>
                      <code className="sa-domain-tab__long-code">{certValidation.name}</code>
                      <button className="sa-domain-tab__copy" onClick={() => copyToClipboard(certValidation.name)} title="Copy">
                        <Copy size={12} />
                      </button>
                    </td>
                    <td>
                      <code className="sa-domain-tab__long-code">{certValidation.value}</code>
                      <button className="sa-domain-tab__copy" onClick={() => copyToClipboard(certValidation.value)} title="Copy">
                        <Copy size={12} />
                      </button>
                    </td>
                    <td>SSL certificate validation</td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="sa-domain-tab__dns-note">
              DNS propagation can take a few minutes to 24 hours. After both records are added, click &quot;Verify &amp; Activate&quot;.
              The SSL certificate is issued automatically once the validation record propagates.
            </p>
          </div>
        )}

        {/* Verified — show success info */}
        {domain && verified && (
          <div className="sa-domain-tab__dns-box sa-domain-tab__dns-box--success">
            <h6><CheckCircle size={14} /> Domain is Live</h6>
            <p>
              <code>{domain}</code> → CloudFront (<code>{cloudFrontDomain}</code>) → your React app → API at <code>api.lmscampus.com</code>
            </p>
          </div>
        )}

        {/* Verification steps */}
        {steps.length > 0 && (
          <div className="sa-domain-tab__steps">
            {steps.map((s, i) => (
              <div key={i} className={`sa-domain-tab__step ${s.startsWith("✓") ? "sa-domain-tab__step--ok" : "sa-domain-tab__step--fail"}`}>
                {s}
              </div>
            ))}
          </div>
        )}

        {/* Action buttons */}
        {domain && (
          <div className="sa-domain-tab__actions">
            {!verified && (
              <AppButton variant="default" size="sm" onClick={handleVerify} disabled={verifying}>
                <RefreshCw size={14} className={verifying ? "spin-icon" : ""} />
                {verifying ? "Verifying..." : "Verify & Activate"}
              </AppButton>
            )}
            {verified && (
              <AppButton variant="outline" size="sm" onClick={handleVerify} disabled={verifying}>
                <RefreshCw size={14} />
                Re-verify
              </AppButton>
            )}
            {!verified && certStatus === "pending" && (
              <AppButton variant="outline" size="sm" onClick={handleRefreshStatus}>
                <RefreshCw size={14} />
                Refresh Status
              </AppButton>
            )}
            <AppButton variant="outline" size="sm" onClick={handleRemove} disabled={removing}>
              <Trash2 size={14} />
              {removing ? "Removing..." : "Remove Domain"}
            </AppButton>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`sa-domain-tab__msg sa-domain-tab__msg--${message.type}`}>
            {message.type === "success" ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            <span style={{ whiteSpace: "pre-line" }}>{message.text}</span>
          </div>
        )}
      </DetailSection>
    </div>
  );
};

export default DomainTab;
